import React, { ReactNode, createContext, useContext, useMemo, useState } from "react";

import MessageBox from "../informative/messagebox";

interface MessageBoxState {
  title?: string;
  message?: string;
  isOpen: boolean;
  content?: ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  width?: string;
  closeAfter?: boolean;
}

interface MessageBoxContextType {
  showMessage: (options: { title: string; message: string; content?: ReactNode; onConfirm?: () => void; onCancel?: () => void; width?: string; closeAfter?: boolean }) => void;
  closeMessage: () => void;
}

const MessageBoxContext = createContext<MessageBoxContextType | undefined>(undefined);

interface MessageBoxProviderProps {
  children: ReactNode;
}

export const MessageBoxProvider: React.FC<MessageBoxProviderProps> = ({ children }) => {
  const [state, setState] = useState<MessageBoxState>({
    isOpen: false,
  });

  const showMessage = ({
    title,
    message,
    content,
    onConfirm,
    onCancel,
    width,
    closeAfter,
  }: {
    title: string;
    message: string;
    content?: ReactNode;
    onConfirm?: () => void;
    onCancel?: () => void;
    width?: string;
    closeAfter?: boolean;
  }) => {
    setState({ title, message, content, onConfirm, onCancel, isOpen: true, width, closeAfter });
  };

  const closeMessage = () => setState((prev) => ({ ...prev, isOpen: false }));

  const contextValue = useMemo(() => ({ showMessage, closeMessage }), [showMessage]);

  return (
    <MessageBoxContext.Provider value={contextValue}>
      {children}
      <MessageBox
        isOpen={state.isOpen}
        title={state.title}
        message={state.message}
        content={state.content}
        onConfirm={state.onConfirm}
        onCancel={state.onCancel}
        onClose={closeMessage}
        width={state.width}
        closeAfter={state.closeAfter}
      />
    </MessageBoxContext.Provider>
  );
};

export const useMessageBox = (): MessageBoxContextType => {
  const context = useContext(MessageBoxContext);
  if (!context) {
    throw new Error("useMessageBox must be used within a MessageBoxProvider");
  }
  return context;
};
